import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, type TaskForAsyncTaskFunction } from 'ember-concurrency';
import type { ColumnHeader } from 'garaje/components/ui/table';
import type EmployeeModel from 'garaje/models/employee';
import { createURN } from 'garaje/models/generic-resource';
import type {
  ResourceInventoryModel,
  ResourcesResourceTypeIndexRouteModel,
} from 'garaje/pods/resources/resource-type/index/route';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { postGenericResources } from 'garaje/utils/map/api/bulk-update-resources';
import _compact from 'lodash/compact';
import { resolve } from 'rsvp';

interface ResourceInventoryListComponentArgs {
  model: ResourcesResourceTypeIndexRouteModel & ResourceInventoryModel;
  employees: EmployeeModel[];
  hasMoreEmployeePages: boolean;
  loadMoreEmployeesTask: TaskForAsyncTaskFunction<unknown, () => Promise<void>>;
  onEmployeeSearchTask: TaskForAsyncTaskFunction<unknown, (term: string) => Promise<void>>;
  setReloadTableCallback: (callback: () => Promise<void>) => void;
}

interface ResourcesDataModel {
  id: number;
  amenities: { id: number; resourceTypeId: number; name: string }[];
  assignments: { employeeId: number }[];
  createdAt: string;
  deletedAt: string | null;
  floorId: number;
  geometry: { [key: string]: unknown };
  name: string;
  relationId: string;
  resourceTypeId: number;
  updatedAt: string;
}

interface ResourceInventoryRecord {
  id: number;
  floorName: string;
  resourceName: string;
  amenities: string;
  assignedTo: string | SafeString;
}

function isResourceUnplaced(
  resource: { geometry?: { pointGeometry?: { coordinates?: unknown[] } } } | undefined,
): boolean {
  return (
    !resource ||
    !resource.geometry ||
    !resource.geometry.pointGeometry ||
    !Array.isArray(resource.geometry.pointGeometry.coordinates) ||
    resource.geometry.pointGeometry.coordinates.length === 0
  );
}

export default class ResourceInventoryListComponent extends Component<ResourceInventoryListComponentArgs> {
  @tracked resourcesData: ResourcesDataModel[] = [];
  @tracked latestResourcesData: ResourcesDataModel[] = [];

  @tracked resourceRecords: ResourceInventoryRecord[] = [];

  @tracked resourcesDataPage: number = 0;
  @tracked resourcesDataLimit: number = 50;

  @tracked isDeleteResourceModalOpen: boolean = false;
  @tracked isEditResourceModalOpen: boolean = false;
  @tracked isDeleteParkingModalOpen: boolean = false;

  @tracked resourceToDelete: ResourcesDataModel | null = null;
  @tracked resourceToEdit: ResourcesDataModel | null = null;
  @tracked employees: EmployeeModel[] = [];
  @tracked editFormData: {
    name: string;
    floor: string;
    assignedEmployeeId?: string;
    amenityIds?: string[];
    coordinates?: number[];
  } | null = null;

  @service declare store: Store;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  constructor(owner: unknown, args: ResourceInventoryListComponentArgs) {
    super(owner, args);

    this.args.setReloadTableCallback(this.refreshInventory.bind(this));
    void this.loadResourceInventoryTable();
  }

  loadAdditionalEmployeesTask = task({ drop: true }, async (): Promise<void> => {
    const assignedEmployeeIds = this.resourcesData.map((resource) => {
      // currently, employees are returned as an array of ids but we don't support
      // multiple assignments for a resource just yet. we will need to change this
      // when the product supports multiple assignments
      const { employeeId } = resource.assignments[0] || {};
      if (!employeeId) {
        return;
      }
      return employeeId;
    });

    // Filter out undefined values and get unique employee IDs
    const uniqueEmployeeIds = [...new Set(assignedEmployeeIds.filter(Boolean))];
    // Check which employees are already in the store
    const employeesToFetch: string[] = [];
    const employeesFromStore: EmployeeModel[] = [];
    uniqueEmployeeIds.forEach((employeeId) => {
      if (employeeId) {
        const employee = this.store.peekRecord('employee', employeeId.toString());
        if (employee) {
          employeesFromStore.push(employee);
        } else {
          employeesToFetch.push(employeeId.toString());
        }
      }
    });
    // Only fetch employees that aren't already in the store
    let fetchedEmployees: EmployeeModel[] = [];
    if (employeesToFetch.length > 0) {
      const employeesData = await this.store.query('employee', {
        filter: {
          locations: this.state.currentLocation.id,
          id: employeesToFetch.join(','),
        },
        fields: {
          employees: 'email,name',
        },
      });
      fetchedEmployees = employeesData.slice();
    }

    // Combine employees from store and newly fetched employees
    this.employees = [...employeesFromStore, ...fetchedEmployees];
  });

  loadResourceInventoryTable = async (): Promise<void> => {
    try {
      await this.loadResourceInventoryTask.perform();
      await this.loadAdditionalEmployeesTask.perform();
      await this.createTableRecords();
    } catch (error) {
      // Ignore task cancellation errors as they're expected when the component is destroyed
      if ((error as Error)?.name !== 'TaskCancelation') {
        throw error;
      }
    }
  };

  loadResourceInventoryTask = task({ drop: true }, async (): Promise<ResourcesDataModel[]> => {
    const { resourceTypeId } = this.args.model;
    const locationId = this.state.currentLocation.id;

    try {
      const response = await this.ajax.request(
        `/a/rms/v2/generics/resources?location-id=${locationId}&resource-type-id=${resourceTypeId}&offset=${
          this.resourcesDataPage * this.resourcesDataLimit
        }&limit=${this.resourcesDataLimit}&sort-by=NAME_ASC`,
        {
          type: 'GET',
          headers: { Accept: 'application/vnd.api+json' },
        },
      );

      const { data } = response as { data: ResourcesDataModel[] };
      this.latestResourcesData = data;
      this.resourcesData = [...this.resourcesData, ...data];
      this.resourcesDataPage++;
      return data;
    } catch (error) {
      return Promise.reject(error as Error);
    }
  });

  async createTableRecords(): Promise<void> {
    const resourceRecords = await Promise.all(
      this.latestResourcesData.map(async (resource) => {
        const floor = await this.store.findRecord('floor', resource.floorId);

        const employee = this.employees.find(
          (employee: EmployeeModel) => employee.id === resource.assignments[0]?.employeeId.toString(),
        ) as EmployeeModel;

        const amenities = _compact(
          resource.amenities.map((amenity) => {
            return this.args.model['amenitiesMap'][amenity.id];
          }),
        );

        return {
          id: resource.id,
          floorId: resource.floorId,
          floorName: floor?.name ?? '',
          resourceName: resource.name,
          amenities: amenities.join(', '),
          assignedTo: employee ? htmlSafe(`${employee?.name}<br>${employee?.email}`) : '',
          resourceTypeId: resource.resourceTypeId,
          geometry: resource.geometry,
        };
      }),
    );

    this.resourceRecords = [...this.resourceRecords, ...resourceRecords];
  }

  @action
  async loadMoreResourceRecords(): Promise<void> {
    if (this.hasMoreResourceRecords) {
      try {
        await this.loadResourceInventoryTable();
      } catch (error) {
        // Ignore task cancellation errors as they're expected when the component is destroyed
        if ((error as Error)?.name !== 'TaskCancelation') {
          throw error;
        }
      }
    }
    return resolve();
  }

  @action
  async refreshInventory(): Promise<void> {
    // Reset pagination and reload
    this.resourcesDataPage = 0;
    this.resourcesData = [];
    this.resourceRecords = [];
    try {
      await this.loadResourceInventoryTable();
    } catch (error) {
      // Ignore task cancellation errors as they're expected when the component is destroyed
      if ((error as Error)?.name !== 'TaskCancelation') {
        throw error;
      }
    }
  }

  get inventoryTableColumns(): ColumnHeader[] {
    return [
      {
        key: 'floorName',
        label: 'Floor',
        sortable: true,
      },
      {
        key: 'resourceName',
        label: 'Name',
        sortable: true,
      },
      {
        key: 'amenities',
        label: 'Amenities',
        sortable: false,
      },
      {
        key: 'assignedTo',
        label: 'Assigned to',
        sortable: false,
      },
    ];
  }

  get hasMoreResourceRecords(): boolean {
    return this.latestResourcesData.length === this.resourcesDataLimit;
  }

  get tableActions(): Array<{
    label: string;
    icon?: string;
    iconClass?: string;
    onAction: (row: ResourceInventoryRecord) => void;
    className?: string;
    disabled?: (row: ResourceInventoryRecord) => boolean;
  }> {
    return [
      {
        label: 'Edit',
        icon: '/assets/images/pencil-dark-full.svg',
        onAction: this.handleEditResource.bind(this),
      },
      {
        label: 'View on Map',
        icon: '/assets/images/sidebar/mainNav-slate/maps.svg',
        onAction: this.handleViewOnMap.bind(this),
        disabled: (row: ResourceInventoryRecord) => {
          const resource = this.resourcesData.find((r) => r.id === row.id);
          return isResourceUnplaced(resource);
        },
      },
      {
        label: 'Delete',
        icon: '/assets/images/trash-red.svg',
        iconClass: 'text-red-40',
        className: 'text-red-40',
        onAction: this.handleDeleteResource.bind(this),
      },
    ];
  }

  @action handleViewOnMap(row: ResourceInventoryRecord): void {
    const resource = this.resourcesData.find((r) => r.id === row.id);
    if (!resource || isResourceUnplaced(resource)) {
      return;
    }

    this.router.transitionTo('spaces.maps.edit.show', {
      queryParams: { floor_id: resource.floorId },
    });
    // TODO: Figure out a way to find the exact resource on the edit map
  }

  @action handleEditResource(row: ResourceInventoryRecord): void {
    const resource = this.resourcesData.find((resource) => resource.id === row.id);
    if (resource) {
      this.resourceToEdit = resource;
      this.isEditResourceModalOpen = true;
    }
  }

  @action closeEditResourceModal(): void {
    this.isEditResourceModalOpen = false;
    this.resourceToEdit = null;
    this.editFormData = null;
  }

  @action handleEditFormDataChange(formData: {
    name: string;
    floor: string;
    assignedEmployeeId?: string;
    amenityIds?: string[];
    coordinates?: number[];
  }): void {
    this.editFormData = formData;
  }

  handleUpdateResourceTask = task({ drop: true }, async () => {
    if (!this.editFormData) {
      return;
    }
    await this.updateResourceTask.perform(this.editFormData, true);
  });

  @action handleDeleteResource(row: ResourceInventoryRecord): void {
    const resource = this.resourcesData.find((resource) => resource.id === row.id);
    if (resource) {
      this.resourceToDelete = resource;
      this.isDeleteResourceModalOpen = true;
    }
  }

  @action closeDeleteResourceModal(): void {
    this.isDeleteResourceModalOpen = false;
    this.resourceToDelete = null;
  }

  updateResourceTask = task(
    { restartable: true },
    async (
      formData: {
        name: string;
        floor: string;
        assignedEmployeeId?: string;
        amenityIds?: string[];
        coordinates?: number[];
      },
      shouldCloseModal: boolean = true,
    ) => {
      const payload = {
        op: 'UPDATE' as const,
        data: {
          id: this.resourceToEdit?.id.toString(),
          name: formData.name,
          floorId: parseInt(formData.floor),
          resourceTypeId: 2,
          amenities: formData.amenityIds?.map((amenityId) => ({ id: parseInt(amenityId) })) || [],
          assignments: formData.assignedEmployeeId ? [{ employeeId: parseInt(formData.assignedEmployeeId) }] : [],
          relationId: createURN('location', this.state.currentLocation.id),
          geometry: {
            type: 'Point',
            coordinates: formData.coordinates || [],
          },
        },
      };
      try {
        await postGenericResources(this.ajax, [payload]);
        if (shouldCloseModal) {
          this.closeEditResourceModal();
        }
        this.flashMessages.showAndHideFlash('success', 'Parking spot saved');
      } catch (error) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(error));
      }
    },
  );

  deleteResourceTask = task({ drop: true }, async (): Promise<void> => {
    if (!this.resourceToDelete) return;
    const resource = this.resourceToDelete;
    const locationId = this.state.currentLocation.id;
    const relationId = createURN('location', locationId);
    const payload = [
      {
        op: 'DELETE' as const,
        data: {
          id: resource.id.toString(),
          name: resource.name,
          geometry: resource.geometry as { type: string; coordinates: number[] },
          resourceTypeId: resource.resourceTypeId,
          amenities: resource.amenities || [],
          assignments: resource.assignments || [],
          floorId: resource.floorId,
          relationId,
        },
      },
    ];

    try {
      await postGenericResources(this.ajax, payload);
      this.flashMessages.showAndHideFlash('success', 'Parking spot deleted');
      this.resourcesData = this.resourcesData.filter((r) => r.id !== resource.id);
      this.resourceRecords = this.resourceRecords.filter((r) => r.id !== resource.id);
    } catch (error) {
      this.flashMessages.showFlash('error', parseErrorForDisplay(error));
    }
    this.isDeleteResourceModalOpen = false;
    this.resourceToDelete = null;
  });
}
